import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { useStore } from "@redotech/react-util/store";
import { personalizedSlipsAccessible } from "@redotech/redo-fulfillment-common/personalized-slip/personalized-slip-access";
import {
  getPaperDimensions,
  Printer,
} from "@redotech/redo-fulfillment-group-common/printer";
import { PageFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { PrintProfileSettings } from "@redotech/redo-fulfillment-outbound-labels-common/print-settings";
import { Preset } from "@redotech/redo-fulfillment-parcel-common/presets";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { t, useTranslation } from "@redotech/redo-i18n";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import MonitorIcon from "@redotech/redo-icons/monitor-01.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import PrinterIcon from "@redotech/redo-icons/printer.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  getCurrentTeam,
  updateCurrentTeam,
} from "@redotech/redo-merchant-app-common/client/team";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { useShippingNewSettings } from "@redotech/redo-merchant-app-common/shipping-new-settings-context";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { toast } from "@redotech/redo-web/alert";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTableColumn } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableCard } from "@redotech/redo-web/arbiter-components/tables/redo-table-card";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { getMacArchitecture } from "@redotech/redo-web/utils/mac-architecture";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import {
  PrinterContext,
  redoConnectComputerIdStore,
} from "../common/printer-context";
import { RedoConnectWarning } from "../common/redo-connect-warning";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import * as printSetupStyles from "./print-setup.module.css";

enum PrinterType {
  LABEL = "label",
  LETTER = "letter",
  PREMIUM = "premium",
  BARCODE = "barcode",
}

type Workstation = NonNullable<OutboundLabelSettings["workstations"]>[number];

export interface WorkstationPrintProfile {
  id: string;
  name: string;
  settings?: PrintProfileSettings;
}

export const defaultPrintProfileId = "default";
export const defaultPrintProfile: WorkstationPrintProfile = {
  id: defaultPrintProfileId,
  name: "Default profile",
};

export function getAssignedPrintProfile(
  workstation: Workstation | null,
  printProfiles: readonly WorkstationPrintProfile[],
): WorkstationPrintProfile {
  const defaultProfile =
    printProfiles.find((profile) => profile.id === defaultPrintProfileId) ??
    defaultPrintProfile;

  return (
    printProfiles.find(
      (profile) => profile.id === workstation?.printProfileId,
    ) ?? defaultProfile
  );
}

interface SavedPrinter {
  printerId: number;
  printerName: string;
}

interface PrinterStatus {
  dotColor?: "red";
  text: string;
  textColor?: "error";
}

const getPrinterStatus = (printer: Printer): PrinterStatus => {
  if (printer.computer.state !== "connected") {
    return {
      dotColor: "red",
      text: t("{{computerName}} is offline", {
        computerName: printer.computer.name || t("Computer"),
      }),
      textColor: "error",
    };
  }
  if (printer.state !== "online") {
    return {
      dotColor: "red",
      text: t("Printer is offline"),
      textColor: "error",
    };
  }
  return {
    text: t("Connected to {{computerName}}", {
      computerName: printer.computer.name || t("Unknown"),
    }),
  };
};

interface RedoConnectComputerStatus {
  color: "error" | "success";
  text: string;
}

const getRedoConnectComputerStatus = (
  computer: Printer["computer"],
): RedoConnectComputerStatus => {
  if (computer.state !== "connected") {
    return { color: "error", text: t("Disconnected") };
  }
  return { color: "success", text: t("Connected") };
};

const getRedoConnectComputerOption = (
  computer: Printer["computer"],
): RedoListItem<number> => {
  const status = getRedoConnectComputerStatus(computer);
  const supportingText = computer.hostname
    ? `${status.text} · ${computer.hostname}`
    : status.text;

  return {
    id: computer.id,
    leadingItem: {
      type: "status",
      color: status.color === "error" ? "red" : "transparent",
    },
    supportingText,
    text: computer.name,
    type: "text",
    value: computer.id,
  };
};

const getSavedPrinter = (
  workstation: Workstation,
  printerType: PrinterType,
): SavedPrinter | undefined => {
  if (printerType === PrinterType.BARCODE) {
    return workstation.barcodePrinterId
      ? {
          printerId: workstation.barcodePrinterId,
          printerName: workstation.barcodePrinterName || t("Configured"),
        }
      : undefined;
  }

  const selectedPrinter = workstation.selectedPrinters.find((printer) => {
    switch (printerType) {
      case PrinterType.LABEL:
        return printer.format === PageFormat.Label && !printer.isPremium;
      case PrinterType.LETTER:
        return printer.format === PageFormat.Letter && !printer.isPremium;
      case PrinterType.PREMIUM:
        return Boolean(printer.isPremium);
      default:
        return false;
    }
  });

  return selectedPrinter
    ? {
        printerId: selectedPrinter.printerId,
        printerName: selectedPrinter.printerName,
      }
    : undefined;
};

export const RedoConnectSettings = memo(function RedoConnectSettings({
  printProfiles,
}: {
  printProfiles: readonly WorkstationPrintProfile[];
}) {
  const { t } = useTranslation();
  const newSettings = useShippingNewSettings();
  const outboundLabelsClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const team = useRequiredContext(TeamContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const reloadTeam = useRequiredContext(ReloadTeamContext);
  const { setPageContext } = useContext(GlobalAgentContext);
  const { loading: printersLoading, printers } = useContext(PrinterContext);
  const personalizedSlipsEnabled = personalizedSlipsAccessible(
    team.settings.outboundLabels,
  );

  useEffect(() => {
    setPageContext?.({
      pageTitle: "Print setup",
      pageDescription:
        "The merchant is on the OMS Print setup and printer configuration settings page",
      preferredAgent: "oms",
      preferredSkill: "oms",
    });
    return () => setPageContext?.(undefined);
  }, [setPageContext]);

  const [printNodeOpen, setPrintNodeOpen] = useState(false);
  const [printNodeSigninOpen, setPrintNodeSigninOpen] = useState(false);
  const [printNodePasswordOpen, setPrintNodePasswordOpen] = useState(false);
  const [computerSelectionOpen, setComputerSelectionOpen] = useState(false);
  const [computerSelectionId, setComputerSelectionId] = useState<
    number | undefined
  >();
  const [redoConnectComputerId, setRedoConnectComputerId] = useStore(
    redoConnectComputerIdStore,
  );
  const [workstationOpen, setWorkstationOpen] = useState(false);
  const [selectedWorkstation, setSelectedWorkstation] =
    useState<Workstation | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [workstationToDelete, setWorkstationToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const printNodeQuery = useRpcQuery(RedoOutboundLabelsRpcClientContext, {
    args: { refresh: true },
    method: "getPrintNodeInfo" as const,
    options: { refetchOnMount: "always" },
  });

  const printNodeInfo = printNodeQuery.data;

  const detectedMac = useLoad(getMacArchitecture, []).value;

  const presetsLoad = useLoad(async () => {
    const result = await outboundLabelsClient.getPresets({});
    return result.presets;
  }, [outboundLabelsClient]);

  const redoConnectComputersLoading = printNodeInfo === undefined;
  const sortedRedoConnectComputers = useMemo(() => {
    if (printNodeInfo == null) {
      return [];
    }
    return [...printNodeInfo.computers].sort((first, second) =>
      first.name.localeCompare(second.name),
    );
  }, [printNodeInfo]);
  const soleRedoConnectComputer =
    sortedRedoConnectComputers.length === 1
      ? sortedRedoConnectComputers[0]
      : undefined;

  useEffect(() => {
    if (
      redoConnectComputersLoading ||
      redoConnectComputerId !== undefined ||
      !soleRedoConnectComputer
    ) {
      return;
    }
    setRedoConnectComputerId(soleRedoConnectComputer.id);
  }, [
    redoConnectComputerId,
    redoConnectComputersLoading,
    setRedoConnectComputerId,
    soleRedoConnectComputer,
  ]);

  const computerOptions: RedoListItem<number>[] = useMemo(
    () => sortedRedoConnectComputers.map(getRedoConnectComputerOption),
    [sortedRedoConnectComputers],
  );
  const selectedComputer = sortedRedoConnectComputers.find(
    (computer) => computer.id === redoConnectComputerId,
  );
  const selectedComputerOption = computerOptions.find(
    (option) => option.value === computerSelectionId,
  );
  const noComputersDetected = sortedRedoConnectComputers.length === 0;
  const selectedComputerUnavailable =
    !redoConnectComputersLoading &&
    redoConnectComputerId !== undefined &&
    selectedComputer === undefined;

  let currentComputerText = t("This computer: Not selected");
  let currentComputerStatus: RedoConnectComputerStatus | undefined;
  if (selectedComputer) {
    currentComputerText = t("This computer: {{computerName}}", {
      computerName: selectedComputer.name,
    });
    currentComputerStatus = getRedoConnectComputerStatus(selectedComputer);
  } else if (selectedComputerUnavailable) {
    currentComputerText = t("Previously selected computer not detected");
    currentComputerStatus = { color: "error", text: t("Unavailable") };
  }
  const computerButtonText =
    redoConnectComputerId === undefined
      ? t("Choose computer")
      : t("Change computer");
  const showComputerSelection =
    sortedRedoConnectComputers.length > 1 ||
    (selectedComputerUnavailable && !noComputersDetected);
  const showComputerDetails =
    !noComputersDetected &&
    (showComputerSelection || currentComputerStatus?.color === "error");
  const computerSelectionDisabledReason =
    selectedComputerOption === undefined
      ? t("Choose a computer before saving.")
      : false;
  const currentComputerStatusBadge = currentComputerStatus ? (
    <RedoBadge
      color={currentComputerStatus.color}
      size="sm"
      text={currentComputerStatus.text}
    />
  ) : null;
  let currentComputerStatusDisplay = currentComputerStatusBadge;
  if (currentComputerStatus?.color === "error") {
    currentComputerStatusDisplay = (
      <Tooltip
        leftAlign
        title={t(
          "Make sure {{productName}} is running on this computer. After opening it, the connection status may take a moment to update.",
          { productName: "Redo Connect" },
        )}
      >
        <Flex align="center" gap="xs">
          {currentComputerStatusBadge}
          <RedoIcon color="tertiary" Icon={InfoCircleIcon} size={16} />
        </Flex>
      </Tooltip>
    );
  }

  const openComputerSelection = useHandler(() => {
    const selectedComputerIsAvailable = sortedRedoConnectComputers.some(
      (computer) => computer.id === redoConnectComputerId,
    );
    setComputerSelectionId(
      selectedComputerIsAvailable ? redoConnectComputerId : undefined,
    );
    setComputerSelectionOpen(true);
  });

  const closeComputerSelection = useHandler(() => {
    setComputerSelectionOpen(false);
  });

  const saveComputerSelection = useHandler(() => {
    if (!selectedComputerOption) {
      return;
    }
    setRedoConnectComputerId(selectedComputerOption.value);
    setComputerSelectionOpen(false);
  });

  const handleDeleteWorkstation = useHandler(async () => {
    if (!workstationToDelete) {
      return;
    }

    try {
      const { team } = await getCurrentTeam(merchantClient, {});

      const workstations = team.settings.outboundLabels.workstations || [];
      const updatedWorkstations = workstations.filter(
        (w) => w._id !== workstationToDelete.id,
      );

      team.settings.outboundLabels.workstations = updatedWorkstations;

      await updateCurrentTeam(merchantClient, { team });
      reloadTeam();
      setDeleteConfirmOpen(false);
      setWorkstationToDelete(null);
    } catch (error) {
      console.error("Error deleting workstation:", error);
    }
  });

  const onDeleteWorkstation = useHandler((id: string, name: string) => {
    setWorkstationToDelete({ id, name });
    setDeleteConfirmOpen(true);
  });

  const desktopClients = (
    <Flex dir="column" gap={newSettings ? "sm" : "xs"}>
      <Text fontSize="sm" fontWeight={newSettings ? "semibold" : undefined}>
        {newSettings ? "Download Redo Connect" : t("Desktop clients:")}
      </Text>
      <RedoExternalLink
        text="Windows"
        url="https://app.printnode.com/download/client/getredo/windows"
      />
      <Flex align="center" gap="xs">
        <RedoExternalLink
          text="MacOS (Apple Silicon)"
          url="https://app.printnode.com/download/client/getredo/osx"
        />
        {detectedMac === "apple-silicon" && (
          <RedoBadge
            color="success"
            size="xs"
            text={newSettings ? "Recommended" : t("Recommended")}
          />
        )}
      </Flex>
      <Flex align="center" gap="xs">
        <RedoExternalLink
          text="MacOS (Intel)"
          url="https://dl.printnode.com/client/getredo/4.28.28/RedoConnect-4.28.28.dmg"
        />
        {detectedMac === "intel" && (
          <RedoBadge
            color="success"
            size="xs"
            text={newSettings ? "Recommended" : t("Recommended")}
          />
        )}
      </Flex>
      <Tooltip
        leftAlign={newSettings}
        subtitle={
          newSettings
            ? "Click on the Apple icon in the top-left corner of your screen and choose the About This Mac drop-down option. This will open a small window in the middle of your screen. If the Chip line says Apple M1, M2, M3, M4, or higher download the Apple Silicon build. If it lists an Intel processor instead, download the Intel build."
            : t(
                "Click on the Apple icon in the top-left corner of your screen and choose the About This Mac drop-down option. This will open a small window in the middle of your screen. If the Chip line says Apple M1, M2, M3, M4, or higher download the Apple Silicon build. If it lists an Intel processor instead, download the Intel build.",
              )
        }
        title={
          newSettings
            ? "How to check which Mac you have"
            : t("How to check which Mac you have")
        }
      >
        <Flex
          align="center"
          alignSelf="flex-start"
          className={newSettings ? undefined : "pt-1"}
          gap="xs"
        >
          <RedoIcon color="tertiary" Icon={InfoCircleIcon} size={14} />
          <Text
            className={printSetupStyles.explainable}
            fontSize="xxs"
            textColor={newSettings ? "secondary" : "tertiary"}
          >
            {newSettings
              ? "Not sure which Mac you have?"
              : t("Not sure which Mac you have?")}
          </Text>
        </Flex>
      </Tooltip>
    </Flex>
  );

  const accountActions = (
    <Flex
      flexDirection="row"
      gap={newSettings ? "sm" : undefined}
      w={newSettings ? "full" : "lg"}
      wrap={newSettings ? "wrap" : undefined}
    >
      <RedoButton
        hierarchy={newSettings ? "secondary" : undefined}
        onClick={() => setPrintNodeSigninOpen(true)}
        text={t("Sign in")}
      />
      <RedoButton
        hierarchy={newSettings ? "secondary" : undefined}
        onClick={() => setPrintNodeOpen(true)}
        text={t("Create account")}
      />
    </Flex>
  );

  return (
    <Flex
      align="center"
      alignSelf="center"
      dir="column"
      gap="xl"
      w={newSettings ? "full" : "xl"}
    >
      {!newSettings && (
        <Text fontSize="md" fontWeight="semibold" w="lg">
          {t("Redo Connect")}
        </Text>
      )}
      {printNodeInfo === undefined ? (
        <Card
          title={newSettings ? "Redo Connect" : undefined}
          w={newSettings ? "full" : "lg"}
        >
          <Flex align="center" gap="sm" justify="center">
            <RedoSpinner size="sm" />
            <Text fontSize="sm" textColor="secondary">
              {t("Loading {{productName}}...", { productName: "Redo Connect" })}
            </Text>
          </Flex>
        </Card>
      ) : printNodeInfo === null ? (
        <>
          {newSettings ? (
            <Card title={t("Redo Connect")} w="full">
              <Text fontSize="xs" textColor="secondary">
                {t(
                  "Install Redo Connect on each computer you print from. Link an account below, then use that account to sign in to the desktop app.",
                )}
              </Text>
              {desktopClients}
              {accountActions}
            </Card>
          ) : (
            accountActions
          )}
          <PrintNodeSetupModal
            onClose={() => setPrintNodeOpen(false)}
            open={printNodeOpen}
          />
          <PrintNodeSigninModal
            onClose={() => setPrintNodeSigninOpen(false)}
            open={printNodeSigninOpen}
          />
        </>
      ) : (
        <>
          <Card
            headerExtra={
              <RedoBadge
                text={newSettings ? t("Account linked") : t("Connected")}
              />
            }
            title={newSettings ? t("Redo Connect") : undefined}
            w={newSettings ? "full" : "lg"}
          >
            <Flex dir="row" justify="space-between" w="full">
              <Text fontSize="sm">
                {t("Email: {{email}}", { email: printNodeInfo.email })}
              </Text>
              <Flex dir="column" gap="sm">
                <Tooltip
                  title={
                    !printNodeInfo.hasPassword
                      ? t(
                          "Password must be updated to support sharing {{productName}} across stores",
                          { productName: "Redo Connect" },
                        )
                      : undefined
                  }
                >
                  <Flex>
                    <RedoButton
                      hierarchy="secondary"
                      onClick={() => setPrintNodePasswordOpen(true)}
                      size="sm"
                      text={t("Change password")}
                      theme="normal"
                    />
                  </Flex>
                </Tooltip>
              </Flex>
            </Flex>
            {desktopClients}
            {noComputersDetected ? (
              <RedoAlert
                color="warning"
                description={t(
                  "Open {{productName}} on this computer, or install a desktop client above.",
                  { productName: "Redo Connect" },
                )}
                fullWidth
                size="sm"
                title={t("No {{productName}} computers detected", {
                  productName: "Redo Connect",
                })}
              />
            ) : null}
            {showComputerDetails ? (
              <Flex align="flex-start" dir="column" gap="xs">
                <Flex align="center" gap="sm">
                  <Text fontSize="sm">{currentComputerText}</Text>
                  {currentComputerStatusDisplay}
                </Flex>
                {showComputerSelection ? (
                  <RedoButton
                    hierarchy="secondary"
                    onClick={openComputerSelection}
                    size="sm"
                    text={computerButtonText}
                    theme="normal"
                  />
                ) : null}
              </Flex>
            ) : null}
            <RedoConnectWarning computerId={redoConnectComputerId} />
          </Card>
          <RedoModal
            isOpen={showComputerSelection && computerSelectionOpen}
            modalSize="sm"
            onModalCloseRequested={closeComputerSelection}
            primaryButton={{
              disabled: computerSelectionDisabledReason,
              onClick: saveComputerSelection,
              size: "xs",
              text: t("Save"),
            }}
            secondaryButton={{
              onClick: closeComputerSelection,
              size: "xs",
              text: t("Cancel"),
            }}
            title={t("Choose this computer")}
          >
            <Flex dir="column" gap="lg" w="full">
              <Text fontSize="sm">
                {t(
                  "Select the {{productName}} client running on this computer.",
                  { productName: "Redo Connect" },
                )}
              </Text>
              <RedoSingleSelectDropdownInput<number>
                label={t("{{productName}} computer", {
                  productName: "Redo Connect",
                })}
                options={computerOptions}
                optionSelected={({ item }) => {
                  setComputerSelectionId(item.value);
                }}
                placeholder={t("Choose a computer")}
                selectedItem={selectedComputerOption}
                size="sm"
              />
            </Flex>
          </RedoModal>
          <RedoTableCard
            className={printSetupStyles.workstationsTableCard}
            rightButtonProps={{
              IconLeading: PlusIcon,
              hierarchy: "secondary",
              onClick: () => setWorkstationOpen(true),
              size: "sm",
              text: t("Add"),
              theme: "normal",
            }}
            tableProps={{
              layout: {
                columns: workstationColumns(
                  onDeleteWorkstation,
                  printers,
                  printersLoading,
                  personalizedSlipsEnabled,
                ),
              },
              rowsOfData: team.settings.outboundLabels?.workstations ?? [],
              emptyStateContent: newSettings ? (
                <Flex px="3xl" py="5xl" w="full">
                  <RedoEmptyState
                    Icon={MonitorIcon}
                    subtext={t(
                      "Assign printers and printing preferences to each workstation.",
                    )}
                    text={t("Set up a workstation")}
                  />
                </Flex>
              ) : (
                <Flex align="center" flexDirection="column" gap="md" p="lg">
                  <Text>{t("No workstations found.")}</Text>
                </Flex>
              ),
              onRowClick: (row) => {
                setSelectedWorkstation(row);
                setWorkstationOpen(true);
              },
              size: "md",
            }}
            title={t("Workstations")}
          />
          <WorkstationSettingsModal
            computerId={redoConnectComputerId}
            key={getKey(selectedWorkstation)}
            onClose={() => {
              setWorkstationOpen(false);
              setSelectedWorkstation(null);
            }}
            open={workstationOpen}
            presets={presetsLoad.value}
            printProfiles={printProfiles}
            workstation={selectedWorkstation}
          />
          <RedoModal
            isOpen={deleteConfirmOpen}
            onModalCloseRequested={() => {
              setDeleteConfirmOpen(false);
              setWorkstationToDelete(null);
            }}
            primaryButton={{
              text: t("Delete"),
              onClick: handleDeleteWorkstation,
              theme: "destructive",
            }}
            secondaryButton={{
              text: t("Cancel"),
              onClick: () => {
                setDeleteConfirmOpen(false);
                setWorkstationToDelete(null);
              },
            }}
            title={t("Delete Workstation")}
          >
            <Text>
              {t(
                'Are you sure you want to delete the workstation "{{workstationName}}"? This action cannot be undone.',
                { workstationName: workstationToDelete?.name },
              )}
            </Text>
          </RedoModal>
          <PrintNodePasswordModal
            email={printNodeInfo?.email}
            onClose={() => setPrintNodePasswordOpen(false)}
            open={printNodePasswordOpen}
          />
        </>
      )}
    </Flex>
  );
});

const printNodeSetupForm = groupInput({
  email: input<string>({ validator: nonEmptyValidator }),
  password: input<string>({ validator: nonEmptyValidator }),
});

const printNodePasswordForm = groupInput({
  newPassword: input<string>({
    validator: (value) => {
      const requiredErrors = nonEmptyValidator(value);
      if (requiredErrors.length > 0) {
        return requiredErrors;
      }
      if (value.length < 8) {
        return [t("Password must be at least 8 characters")];
      }
      if (value.length > 100) {
        return [t("Password must be 100 characters or fewer")];
      }
      return [];
    },
  }),
});

export const PrintNodeSetupModal = memo(function PrintNodeSetupModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const newSettings = useShippingNewSettings();
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const user = useContext(UserContext);
  const input = useInput(printNodeSetupForm, {
    email: user?.email ?? "",
    password: "",
  });

  const handleCreate = useHandler(async () => {
    const { email, password } = input.value;
    const { success, message } = await client.createPrintNodeAccount({
      email,
      password,
    });
    if (success) {
      location.reload();
    } else {
      toast(message ?? t("Failed to create account"), { variant: "error" });
    }
  });

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: t("Signup"),
        onClick: handleCreate,
        disabled: input.allErrors.length > 0,
      }}
      secondaryButton={{ text: t("Close"), onClick: onClose }}
      title={t("{{productName}} Setup", { productName: "Redo Connect" })}
    >
      <Flex flexDirection="column" gap="sm">
        <RedoTextInput
          description={
            newSettings
              ? t("Use this email to sign in to the desktop Redo Connect app.")
              : undefined
          }
          inputTypeHint="email"
          label={t("Email")}
          setValue={input.inputs.email.setValue}
          value={input.inputs.email.value}
        />
        <RedoTextInput
          description={t(
            "You'll use this password to sign into {{productName}} on your desktop",
            { productName: "Redo Connect" },
          )}
          inputTypeHint="password"
          label={t("Password")}
          setValue={input.inputs.password.setValue}
          value={input.inputs.password.value}
        />
      </Flex>
    </RedoModal>
  );
});

export const PrintNodeSigninModal = memo(function PrintNodeSigninModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const newSettings = useShippingNewSettings();
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const input = useInput(printNodeSetupForm, { email: "", password: "" });

  const handleSignin = useHandler(async () => {
    const { email, password } = input.value;
    const { success, message } = await client.signInPrintNodeAccount({
      email,
      password,
    });
    if (success) {
      location.reload();
    } else {
      toast(message, { variant: "error" });
    }
  });

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: t("Signin"),
        onClick: handleSignin,
        disabled: input.allErrors.length > 0,
      }}
      secondaryButton={{ text: t("Close"), onClick: onClose }}
      title={t("{{productName}} Setup", { productName: "Redo Connect" })}
    >
      <Flex flexDirection="column" gap="sm">
        <RedoTextInput
          description={
            newSettings
              ? t("Use this email to sign in to the desktop Redo Connect app.")
              : undefined
          }
          inputTypeHint="email"
          label={t("Email")}
          setValue={input.inputs.email.setValue}
          value={input.inputs.email.value}
        />
        <RedoTextInput
          description={
            newSettings
              ? t("Enter the password for your Redo Connect account.")
              : undefined
          }
          inputTypeHint="password"
          label={t("Password")}
          setValue={input.inputs.password.setValue}
          value={input.inputs.password.value}
        />
      </Flex>
    </RedoModal>
  );
});

export const PrintNodePasswordModal = memo(function PrintNodePasswordModal({
  open,
  onClose,
  email,
}: {
  open: boolean;
  onClose: () => void;
  email: string;
}) {
  const { t } = useTranslation();
  const newSettings = useShippingNewSettings();
  const input = useInput(printNodePasswordForm, { newPassword: "" });

  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const reloadTeam = useRequiredContext(ReloadTeamContext);
  const handleSave = useHandler(async () => {
    const { newPassword } = input.value;
    const { success, message } = await client.updatePrintNodePassword({
      email,
      newPassword,
    });
    if (success) {
      toast(t("Password updated"), { variant: "success" });
      reloadTeam();
      onClose();
    } else {
      toast(message, { variant: "error" });
    }
  });

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: t("Save"),
        onClick: handleSave,
        disabled: input.allErrors.length > 0,
      }}
      secondaryButton={{ text: t("Close"), onClick: onClose }}
      title={t("Reset {{productName}} password", {
        productName: "Redo Connect",
      })}
    >
      <Flex flexDirection="column" gap="sm">
        <RedoTextInput
          description={
            newSettings
              ? t("Set a new password for signing in to Redo Connect.")
              : t("Must be 8–100 characters")
          }
          errorText={
            !newSettings && input.inputs.newPassword.changed
              ? input.inputs.newPassword.errors[0]
              : undefined
          }
          inputTypeHint="password"
          label={t("New Password")}
          setValue={input.inputs.newPassword.setValue}
          value={input.inputs.newPassword.value}
        />
      </Flex>
    </RedoModal>
  );
});

const printerColumn = (
  key: string,
  header: string,
  printerType: PrinterType,
  printers: Printer[],
  loading: boolean,
): RedoTableColumn<Workstation> => ({
  key,
  renderHeaderCell: () => <StandardRedoTableHeaderCell headerText={header} />,
  renderNormalCell: ({ row }) => {
    const savedPrinter = getSavedPrinter(row, printerType);
    if (!savedPrinter) {
      return <RedoTableTextCell text={t("Download PDF")} />;
    }

    const printer = printers.find(
      (candidate) => candidate.id === savedPrinter.printerId,
    );
    if (!printer) {
      return (
        <RedoTableTextCell
          subtext={loading ? undefined : t("No longer available — reselect")}
          text={savedPrinter.printerName}
          textColor={loading ? undefined : "error"}
        />
      );
    }

    const status = getPrinterStatus(printer);
    return (
      <RedoTableTextCell
        subtext={status.text}
        text={printer.description || printer.name}
        textColor={status.textColor}
      />
    );
  },
});

const workstationColumns = (
  onDelete: (id: string, name: string) => void,
  printers: Printer[],
  loading: boolean,
  personalizedSlipsEnabled: boolean,
): RedoTableColumn<Workstation>[] => {
  const premiumPrinterColumns: RedoTableColumn<Workstation>[] = [];

  if (personalizedSlipsEnabled) {
    premiumPrinterColumns.push(
      printerColumn(
        "premium-printer",
        t("Premium"),
        PrinterType.PREMIUM,
        printers,
        loading,
      ),
    );
  }

  return [
    {
      key: "name",
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText={t("Name")} />
      ),
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell leading text={row.name} />
      ),
    },
    printerColumn(
      "label-printer",
      t("Label"),
      PrinterType.LABEL,
      printers,
      loading,
    ),
    printerColumn(
      "letter-printer",
      t("Letter"),
      PrinterType.LETTER,
      printers,
      loading,
    ),
    ...premiumPrinterColumns,
    printerColumn(
      "barcode-printer",
      t("Barcode"),
      PrinterType.BARCODE,
      printers,
      loading,
    ),
    {
      key: "actions",
      widthMode: { type: "fixed", width: "50px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }) => (
        <Flex align="center" justify="flex-end">
          <RedoButton
            aria-label={t("Delete")}
            IconLeading={TrashIcon}
            onClick={(e) => {
              e.stopPropagation();
              if (row._id) {
                onDelete(row._id, row.name);
              }
            }}
            size="xs"
            theme="destructive"
          />
        </Flex>
      ),
    },
  ];
};

const printerSettingsForm = groupInput({
  printerId: input<number>(),
  printerName: input<string>(),
  paper: input<string>(),
  bin: input<string>(),
  rotate: input<0 | 90 | 180 | 270>(),
  fit_to_page: input<boolean>(),
  useRaw: input<boolean>(),
  dpi: input<string>(),
  duplex: input<"long-edge" | "short-edge" | "one-sided" | "">(),
});
type PrinterSettingsForm = InputSchema.Input<typeof printerSettingsForm>;

const workstationSettingsForm = groupInput({
  name: input<string>({ validator: nonEmptyValidator }),
  printProfileId: input<string>(),
  labelPrinter: printerSettingsForm,
  letterPrinter: printerSettingsForm,
  premiumPrinter: printerSettingsForm,
  barcodePrinter: printerSettingsForm,
  defaultPresetId: input<string | null>(),
  returnsBinPrefix: input<string>(),
  returnsDefaultAisle: input<string>(),
});

enum WorkstationSettingsTab {
  PRINTING = "printing",
  RETURNS = "returns",
}

export const WorkstationSettingsModal = memo(function WorkstationSettingsModal({
  computerId,
  open,
  onClose,
  workstation,
  presets,
  printProfiles,
}: {
  computerId?: number;
  open: boolean;
  onClose: () => void;
  workstation: Workstation | null;
  presets?: Preset[];
  printProfiles: readonly WorkstationPrintProfile[];
}) {
  const { t } = useTranslation();
  const newSettings = useShippingNewSettings();
  const { printers, loading } = useContext(PrinterContext);
  const team = useRequiredContext(TeamContext);
  const isWmsSourceOfTruth =
    !!team.settings.outboundLabels?.wms?.sourceOfTruthEnabled;

  const [configuringPrinter, setConfiguringPrinter] =
    useState<PrinterType | null>(null);
  const [settingsTab, setSettingsTab] = useState<WorkstationSettingsTab>(
    WorkstationSettingsTab.PRINTING,
  );
  const showReturnsTab =
    isWmsSourceOfTruth && settingsTab === WorkstationSettingsTab.RETURNS;

  const selectedLabelPrinter = workstation?.selectedPrinters?.find(
    (printer) => printer.format === PageFormat.Label && !printer.isPremium,
  );
  const selectedLetterPrinter = workstation?.selectedPrinters?.find(
    (printer) => printer.format === PageFormat.Letter && !printer.isPremium,
  );
  const selectedPremiumPrinter = workstation?.selectedPrinters?.find(
    (printer) => printer.isPremium,
  );
  const assignedPrintProfile = getAssignedPrintProfile(
    workstation,
    printProfiles,
  );
  const input = useInput(workstationSettingsForm, {
    name: workstation?.name ?? "",
    printProfileId: assignedPrintProfile.id,
    labelPrinter: {
      printerId: selectedLabelPrinter?.printerId ?? 0,
      printerName: selectedLabelPrinter?.printerName ?? "",
      paper: selectedLabelPrinter?.printerOptions?.paper ?? "",
      bin: selectedLabelPrinter?.printerOptions?.bin ?? "",
      rotate: selectedLabelPrinter?.printerOptions?.rotate ?? 0,
      fit_to_page: selectedLabelPrinter?.printerOptions?.fit_to_page ?? false,
      useRaw: selectedLabelPrinter?.printerOptions?.useRaw ?? false,
      dpi: selectedLabelPrinter?.printerOptions?.dpi ?? "",
      duplex: selectedLabelPrinter?.printerOptions?.duplex ?? ("" as const),
    },
    barcodePrinter: {
      printerId: workstation?.barcodePrinterId ?? 0,
      printerName: workstation?.barcodePrinterName ?? "",
      paper: workstation?.barcodePrinterOptions?.paper ?? "",
      bin: workstation?.barcodePrinterOptions?.bin ?? "",
      rotate: workstation?.barcodePrinterOptions?.rotate ?? 0,
      fit_to_page: workstation?.barcodePrinterOptions?.fit_to_page ?? false,
      useRaw: workstation?.barcodePrinterOptions?.useRaw ?? false,
      dpi: workstation?.barcodePrinterOptions?.dpi ?? "",
      duplex: (workstation?.barcodePrinterOptions?.duplex ??
        "") as DuplexOption,
    },
    letterPrinter: {
      printerId: selectedLetterPrinter?.printerId ?? 0,
      printerName: selectedLetterPrinter?.printerName ?? "",
      paper: selectedLetterPrinter?.printerOptions?.paper ?? "",
      bin: selectedLetterPrinter?.printerOptions?.bin ?? "",
      rotate: selectedLetterPrinter?.printerOptions?.rotate ?? 0,
      fit_to_page: selectedLetterPrinter?.printerOptions?.fit_to_page ?? false,
      useRaw: selectedLetterPrinter?.printerOptions?.useRaw ?? false,
      dpi: selectedLetterPrinter?.printerOptions?.dpi ?? "",
      duplex: selectedLetterPrinter?.printerOptions?.duplex ?? ("" as const),
    },
    premiumPrinter: {
      printerId: selectedPremiumPrinter?.printerId ?? 0,
      printerName: selectedPremiumPrinter?.printerName ?? "",
      paper: selectedPremiumPrinter?.printerOptions?.paper ?? "",
      bin: selectedPremiumPrinter?.printerOptions?.bin ?? "",
      rotate: selectedPremiumPrinter?.printerOptions?.rotate ?? 0,
      fit_to_page: selectedPremiumPrinter?.printerOptions?.fit_to_page ?? true,
      useRaw: selectedPremiumPrinter?.printerOptions?.useRaw ?? false,
      dpi: selectedPremiumPrinter?.printerOptions?.dpi ?? "",
      duplex: selectedPremiumPrinter?.printerOptions?.duplex ?? ("" as const),
    },
    defaultPresetId: workstation?.defaultPresetId ?? null,
    returnsBinPrefix: workstation?.returnsBinPrefix ?? "",
    returnsDefaultAisle: workstation?.returnsDefaultAisle ?? "",
  });

  const printProfileOptions: RedoListItem<string>[] = useMemo(
    () =>
      printProfiles.map((profile) => ({
        id: profile.id,
        text: profile.name,
        type: "text",
        value: profile.id,
      })),
    [printProfiles],
  );
  const selectedPrintProfileOption =
    printProfileOptions.find(
      (option) => option.value === input.inputs.printProfileId.value,
    ) ?? printProfileOptions[0];

  const presetOptions: RedoListItem<string | null>[] = useMemo(() => {
    const options: RedoListItem<string | null>[] = [
      { id: "none", value: null, type: "text", text: t("No default preset") },
    ];
    if (presets) {
      for (const preset of presets) {
        options.push({
          id: preset._id,
          value: preset._id,
          type: "text",
          text: preset.name,
        });
      }
    }
    return options;
  }, [presets, t]);

  const selectedPresetOption = useMemo(() => {
    return (
      presetOptions.find(
        (opt) => opt.value === input.inputs.defaultPresetId.value,
      ) ?? presetOptions[0]
    );
  }, [presetOptions, input.inputs.defaultPresetId.value]);

  const reloadTeam = useRequiredContext(ReloadTeamContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);

  const saveHandler = useHandler(async () => {
    const { team } = await getCurrentTeam(merchantClient, {});

    const value = input.value;

    const paperDimsFor = (
      printerId: number,
      paper: string | undefined,
    ): [number, number] | undefined => {
      if (!paper) {
        return undefined;
      }
      const printer = printers.find((p) => p.id === printerId);
      return printer ? getPaperDimensions(printer, paper) : undefined;
    };

    const selectedPrinters = [];
    if (value.labelPrinter.printerId) {
      selectedPrinters.push({
        format: PageFormat.Label,
        printerId: value.labelPrinter.printerId,
        printerName: value.labelPrinter.printerName,
        printerOptions: {
          paper: value.labelPrinter.paper,
          paperDims: paperDimsFor(
            value.labelPrinter.printerId,
            value.labelPrinter.paper,
          ),
          bin: value.labelPrinter.bin || undefined,
          rotate: value.labelPrinter.rotate,
          fit_to_page: value.labelPrinter.fit_to_page,
          useRaw: value.labelPrinter.useRaw,
          dpi: value.labelPrinter.dpi || undefined,
          duplex: value.labelPrinter.duplex || undefined,
        },
      });
    }
    if (value.letterPrinter.printerId) {
      selectedPrinters.push({
        format: PageFormat.Letter,
        printerId: value.letterPrinter.printerId,
        printerName: value.letterPrinter.printerName,
        printerOptions: {
          paper: value.letterPrinter.paper || undefined,
          paperDims: paperDimsFor(
            value.letterPrinter.printerId,
            value.letterPrinter.paper,
          ),
          bin: value.letterPrinter.bin || undefined,
          rotate: value.letterPrinter.rotate,
          fit_to_page: value.letterPrinter.fit_to_page,
          useRaw: value.letterPrinter.useRaw,
          dpi: value.letterPrinter.dpi || undefined,
          duplex: value.letterPrinter.duplex || undefined,
        },
      });
    }
    if (value.premiumPrinter.printerId) {
      selectedPrinters.push({
        format: PageFormat.Letter,
        printerId: value.premiumPrinter.printerId,
        printerName: value.premiumPrinter.printerName,
        isPremium: true,
        printerOptions: {
          paper: value.premiumPrinter.paper || undefined,
          paperDims: paperDimsFor(
            value.premiumPrinter.printerId,
            value.premiumPrinter.paper,
          ),
          bin: value.premiumPrinter.bin || undefined,
          rotate: value.premiumPrinter.rotate,
          fit_to_page: value.premiumPrinter.fit_to_page,
          useRaw: value.premiumPrinter.useRaw,
          dpi: value.premiumPrinter.dpi || undefined,
          duplex: value.premiumPrinter.duplex || undefined,
        },
      });
    }

    const workstationValue: Omit<Workstation, "_id"> = {
      name: value.name,
      selectedPrinters,
    };
    if (workstation?.requireReceivingSource) {
      workstationValue.requireReceivingSource = true;
    }
    if (value.returnsBinPrefix.trim()) {
      workstationValue.returnsBinPrefix = value.returnsBinPrefix.trim();
    }
    if (value.returnsDefaultAisle.trim()) {
      workstationValue.returnsDefaultAisle = value.returnsDefaultAisle
        .trim()
        .toUpperCase();
    }
    const selectedPrintProfile =
      printProfiles.find((profile) => profile.id === value.printProfileId) ??
      defaultPrintProfile;
    if (selectedPrintProfile.id !== defaultPrintProfileId) {
      workstationValue.printProfileId = selectedPrintProfile.id;
    }
    if (value.barcodePrinter.printerId) {
      workstationValue.barcodePrinterId = value.barcodePrinter.printerId;
      workstationValue.barcodePrinterName = value.barcodePrinter.printerName;
      workstationValue.barcodePrinterOptions = {
        paper: value.barcodePrinter.paper || undefined,
        paperDims: paperDimsFor(
          value.barcodePrinter.printerId,
          value.barcodePrinter.paper,
        ),
        bin: value.barcodePrinter.bin || undefined,
        rotate: value.barcodePrinter.rotate,
        fit_to_page: value.barcodePrinter.fit_to_page,
        useRaw: value.barcodePrinter.useRaw,
        dpi: value.barcodePrinter.dpi || undefined,
        duplex: value.barcodePrinter.duplex || undefined,
      };
    }
    if (value.defaultPresetId !== null) {
      workstationValue.defaultPresetId = value.defaultPresetId;
    }

    const workstations = team.settings.outboundLabels.workstations || [];
    if (workstation?._id) {
      const index = workstations.findIndex((w) => w._id === workstation._id);

      if (index !== -1) {
        workstations[index] = { _id: workstation._id, ...workstationValue };
      }
    } else {
      workstations.push(workstationValue);
    }

    team.settings.outboundLabels.workstations = workstations;

    await updateCurrentTeam(merchantClient, { team });
    reloadTeam();
    onClose();
  });

  const handleModalClose = () => {
    if (configuringPrinter) {
      setConfiguringPrinter(null);
    } else {
      onClose();
    }
  };

  const saveDisabledReason = !input.inputs.name.value.trim()
    ? t("Enter a workstation name before saving.")
    : input.allErrors.length > 0
      ? t("Fix the workstation settings before saving.")
      : false;

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={handleModalClose}
      primaryButton={{
        text: t("Save"),
        onClick: saveHandler,
        disabled: saveDisabledReason,
      }}
      secondaryButton={
        configuringPrinter
          ? { text: t("Back"), onClick: () => setConfiguringPrinter(null) }
          : undefined
      }
      subtitle={workstation?.name}
      title={t("Workstation settings")}
    >
      <Flex flexDirection="column" gap="sm">
        <RedoConnectWarning computerId={computerId} />
        <RedoTextInput
          label={t("Name")}
          setValue={input.inputs.name.setValue}
          value={input.inputs.name.value}
        />
        {!configuringPrinter && isWmsSourceOfTruth && (
          <RedoHorizontalTabs<WorkstationSettingsTab>
            pl="none"
            selectedTab={settingsTab}
            setSelectedTab={setSettingsTab}
            size="sm"
            tabs={[
              { key: WorkstationSettingsTab.PRINTING, label: t("Printing") },
              { key: WorkstationSettingsTab.RETURNS, label: t("Returns") },
            ]}
          />
        )}
        {configuringPrinter ? (
          <PrinterSettingsFormSection
            configuringPrinter={configuringPrinter}
            input={input}
            loading={loading}
            printers={printers}
          />
        ) : showReturnsTab ? (
          <>
            <RedoTextInput
              description={t(
                "Returned items default to the receiving bin named with this prefix plus the aisle of the item's pick bin, for example RETURNS-4A. Doubled aisles like AA also match a single-letter bin.",
              )}
              label={t("Returns bin prefix")}
              placeholder={t("RETURNS-4")}
              setValue={input.inputs.returnsBinPrefix.setValue}
              value={input.inputs.returnsBinPrefix.value}
            />
            <RedoTextInput
              description={t(
                "Aisle used when an item has no pick bin, or its aisle has no returns bin here.",
              )}
              label={t("Default returns aisle")}
              placeholder={t("I")}
              setValue={input.inputs.returnsDefaultAisle.setValue}
              value={input.inputs.returnsDefaultAisle.value}
            />
          </>
        ) : (
          <>
            <RedoSingleSelectDropdownInput<string>
              description={
                newSettings
                  ? t(
                      "Apply this profile's document settings when printing from this workstation.",
                    )
                  : undefined
              }
              label={t("Print profile")}
              options={printProfileOptions}
              optionSelected={({ item }) => {
                input.inputs.printProfileId.setValue(item.value);
              }}
              selectedItem={selectedPrintProfileOption}
              size="xs"
            />
            {presets && presets.length > 0 && (
              <RedoSingleSelectDropdownInput<string | null>
                description={
                  newSettings
                    ? t(
                        "Choose the fulfillment preset used by default at this workstation.",
                      )
                    : undefined
                }
                label={t("Default preset")}
                options={presetOptions}
                optionSelected={({ item }) => {
                  input.inputs.defaultPresetId.setValue(item.value);
                }}
                selectedItem={selectedPresetOption}
                size="xs"
              />
            )}
            <Text fontSize="sm" fontWeight="semibold" mb="md">
              {t("Printer Configuration")}
            </Text>
            <Flex
              className={printSetupStyles.printerCardsContainer}
              dir="row"
              gap="md"
              justify="space-between"
            >
              <PrinterCard
                input={input.inputs.labelPrinter}
                loading={loading}
                name={t("Label Printer")}
                onClick={() => setConfiguringPrinter(PrinterType.LABEL)}
                printers={printers}
              />
              <PrinterCard
                input={input.inputs.letterPrinter}
                loading={loading}
                name={t("Letter Printer")}
                onClick={() => setConfiguringPrinter(PrinterType.LETTER)}
                printers={printers}
              />

              {personalizedSlipsAccessible(team.settings.outboundLabels) && (
                <PrinterCard
                  input={input.inputs.premiumPrinter}
                  loading={loading}
                  name={t("Premium Printer")}
                  onClick={() => setConfiguringPrinter(PrinterType.PREMIUM)}
                  printers={printers}
                />
              )}
              <PrinterCard
                input={input.inputs.barcodePrinter}
                loading={loading}
                name={t("Barcode Printer")}
                onClick={() => setConfiguringPrinter(PrinterType.BARCODE)}
                printers={printers}
              />
            </Flex>
          </>
        )}
      </Flex>
    </RedoModal>
  );
});

const PrinterCard = memo(function PrinterCard({
  name,
  printers,
  loading,
  input,
  onClick,
}: {
  name: string;
  printers: Printer[];
  loading: boolean;
  input: PrinterSettingsForm;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const selectedPrinter = printers.find(
    (printer) => printer.id === input.inputs.printerId.value,
  );

  const isConfigured = Boolean(input.inputs.printerId.value);
  const printerMissing = isConfigured && !loading && !selectedPrinter;
  const printerMissingText = t("Printer no longer available — reselect");
  const selectedPrinterStatus = selectedPrinter
    ? getPrinterStatus(selectedPrinter)
    : undefined;
  let errorText: string | undefined;
  if (printerMissing) {
    errorText = printerMissingText;
  } else if (selectedPrinterStatus?.textColor === "error") {
    errorText = selectedPrinterStatus.text;
  }
  const printerName =
    selectedPrinter?.description ||
    selectedPrinter?.name ||
    input.inputs.printerName.value ||
    t("Configured");
  const statusText = printerMissing
    ? printerMissingText
    : errorText
      ? t("{{printerName}} - {{errorText}}", { printerName, errorText })
      : printerName;
  const statusTextColor = errorText ? "error" : undefined;

  const cardClassNames = [printSetupStyles.printerCard];
  const iconContainerClassNames = [printSetupStyles.printerIconContainer];
  if (isConfigured) {
    cardClassNames.push(printSetupStyles.printerCardConfigured);
    iconContainerClassNames.push(printSetupStyles.printerIconConfigured);
    if (errorText) {
      cardClassNames.push(printSetupStyles.printerCardError);
      iconContainerClassNames.push(printSetupStyles.printerIconError);
    }
  } else {
    iconContainerClassNames.push(printSetupStyles.printerIconUnconfigured);
  }

  return (
    // design-allow: existing printer card click target from main; this rebase does not change it
    <div className={cardClassNames.join(" ")} onClick={onClick}>
      <Flex align="center" dir="column" gap="sm" p="lg" textAlign="center">
        <div className={iconContainerClassNames.join(" ")}>
          <PrinterIcon className={printSetupStyles.printerIcon} />
        </div>
        <Text fontSize="sm" fontWeight="semibold">
          {name}
        </Text>
        {isConfigured ? (
          <Text fontSize="xs" textColor={statusTextColor}>
            {statusText}
          </Text>
        ) : (
          <Text fontSize="xs" textColor="secondary">
            {t("Click to configure")}
          </Text>
        )}
      </Flex>
    </div>
  );
});

const getPrinterDisplayName = (printerType: PrinterType): string => {
  switch (printerType) {
    case PrinterType.LABEL:
      return t("Label Printer");
    case PrinterType.LETTER:
      return t("Letter Printer");
    case PrinterType.PREMIUM:
      return t("Premium Printer");
    case PrinterType.BARCODE:
      return t("Barcode Printer");
    default:
      return t("Unknown Printer");
  }
};

const getPrinterInput = (
  printerType: PrinterType,
  input: InputSchema.Input<typeof workstationSettingsForm>,
): PrinterSettingsForm => {
  switch (printerType) {
    case PrinterType.LABEL:
      return input.inputs.labelPrinter;
    case PrinterType.LETTER:
      return input.inputs.letterPrinter;
    case PrinterType.PREMIUM:
      return input.inputs.premiumPrinter;
    case PrinterType.BARCODE:
      return input.inputs.barcodePrinter;
    default:
      return input.inputs.labelPrinter;
  }
};

const getPrinterPageFormat = (printerType: PrinterType): PageFormat => {
  switch (printerType) {
    case PrinterType.LABEL:
    case PrinterType.BARCODE:
      return PageFormat.Label;
    case PrinterType.LETTER:
    case PrinterType.PREMIUM:
      return PageFormat.Letter;
    default:
      return PageFormat.Letter;
  }
};

const PrinterSettingsFormSection = memo(function PrinterSettingsFormSection({
  configuringPrinter,
  input,
  printers,
  loading,
}: {
  configuringPrinter: PrinterType;
  input: InputSchema.Input<typeof workstationSettingsForm>;
  printers: Printer[];
  loading: boolean;
}) {
  const printerInput = getPrinterInput(configuringPrinter, input);
  const pageFormat = getPrinterPageFormat(configuringPrinter);

  return (
    <PrinterSettingsFormComponent
      input={printerInput}
      loading={loading}
      name={getPrinterDisplayName(configuringPrinter)}
      pageFormat={pageFormat}
      printers={printers}
    />
  );
});

const PrinterSettingsFormComponent = memo(
  function PrinterSettingsFormComponent({
    name,
    printers,
    loading,
    input,
    pageFormat,
  }: {
    name: string;
    printers: Printer[];
    loading: boolean;
    input: PrinterSettingsForm;
    pageFormat: PageFormat;
  }) {
    const { t } = useTranslation();
    const newSettings = useShippingNewSettings();
    const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

    const availablePrinterOptions: RedoListItem<null | Printer>[] = useMemo(
      () => [
        {
          id: "pdf-download",
          leadingItem: { type: "status", color: "transparent" },
          value: null,
          type: "text",
          text: t("Download PDF"),
        },
        ...printers.map<RedoListItem<Printer>>((printer) => {
          const status = getPrinterStatus(printer);
          const displayName = printer.description || printer.name;
          let supportingText = status.text;
          if (printer.name !== displayName) {
            supportingText = `${printer.name} · ${status.text}`;
          }

          return {
            id: printer.id,
            leadingItem: {
              type: "status",
              color: status.dotColor ?? "transparent",
            },
            supportingText,
            text: displayName,
            type: "text",
            value: printer,
          };
        }),
      ],
      [printers, t],
    );

    const configuredPrinterOption = availablePrinterOptions.find(
      (option) => option.value?.id === input.inputs.printerId.value,
    );
    const savedPrinterMissing =
      !loading &&
      Boolean(input.inputs.printerId.value) &&
      !configuredPrinterOption;
    const missingPrinterOptionId = `missing-printer-${input.inputs.printerId.value}`;
    const missingPrinterOption: RedoListItem<null | Printer> | undefined =
      savedPrinterMissing
        ? {
            id: missingPrinterOptionId,
            leadingItem: { type: "status", color: "red" },
            supportingText: t("No longer available — choose a replacement"),
            text: input.inputs.printerName.value || t("Saved printer"),
            type: "text",
            value: null,
          }
        : undefined;
    const printerOptions = missingPrinterOption
      ? [missingPrinterOption, ...availablePrinterOptions]
      : availablePrinterOptions;
    const selectedPrinter =
      missingPrinterOption || configuredPrinterOption || printerOptions[0];

    const paperOptions: RedoListItem<string | null>[] = useMemo(
      () => [
        { id: "default", value: null, type: "text", text: t("Default") },
        ...Object.keys(selectedPrinter?.value?.capabilities?.papers ?? {}).map<
          RedoListItem<string>
        >((key) => ({ id: key, type: "text", value: key, text: key })),
      ],
      [selectedPrinter, t],
    );

    const selectedPaper =
      paperOptions.find(({ value }) => value === input.inputs.paper.value) ||
      paperOptions[0];

    const savedPaper = input.inputs.paper.value;
    const savedPaperUnavailable =
      !!savedPaper &&
      !paperOptions.some((option) => option.value === savedPaper);
    const savedPaperComputer = selectedPrinter?.value?.computer.name;

    const binOptions: RedoListItem<string | null>[] = useMemo(
      () => [
        { id: "default", value: null, type: "text", text: t("Default") },
        ...(selectedPrinter?.value?.capabilities?.bins || []).map((bin) => ({
          id: bin,
          type: "text" as const,
          text: bin,
          value: bin,
        })),
      ],
      [selectedPrinter, t],
    );

    const selectedBin =
      binOptions.find(({ value }) => value === input.inputs.bin.value) ||
      binOptions[0];

    const dpiOptions: RedoListItem<string | null>[] = useMemo(
      () => [
        { id: "default", type: "text", text: t("Default"), value: null },
        ...(selectedPrinter?.value?.capabilities?.dpis?.map((dpi) => ({
          id: dpi,
          type: "text" as const,
          text: dpi,
          value: dpi,
        })) || []),
      ],
      [selectedPrinter, t],
    );

    const selectedDpi =
      dpiOptions.find(({ value }) => value === input.inputs.dpi.value) ||
      dpiOptions[0];

    const [testPrintLoad, doTestPrint] = useTriggerLoad(async () => {
      await client.printTest({
        printerId: input.value.printerId,
        paper: selectedPaper?.value ?? undefined,
        bin: selectedBin?.value ?? undefined,
        rotate: input.value.rotate,
        fit_to_page: input.value.fit_to_page,
        useRaw: input.value.useRaw,
        dpi: input.value.dpi || undefined,
        duplex: input.value.duplex || undefined,
        pageFormat,
      });
    });

    return (
      <Flex flexDirection="column" gap="sm" mt="lg">
        <Text fontSize="sm" mb="md">
          {name}
        </Text>
        <RedoSingleSelectDropdownInput<Printer | null>
          description={
            savedPrinterMissing
              ? newSettings
                ? "Select a replacement printer or Download PDF."
                : t("Select a replacement printer or Download PDF.")
              : newSettings
                ? "Choose a connected printer, or select Download PDF instead."
                : undefined
          }
          disabledItems={
            savedPrinterMissing ? missingPrinterOptionId : undefined
          }
          label={t("Printer")}
          options={printerOptions}
          optionSelected={({ item }) => {
            input.inputs.printerId.setValue(item.value?.id ?? 0);
            input.inputs.printerName.setValue(
              (item.value?.description || item.value?.name) ?? "",
            );
            input.inputs.paper.setValue("");
            input.inputs.bin.setValue("");
            input.inputs.dpi.setValue("");
          }}
          placeholder={t("Select printer")}
          selectedItem={selectedPrinter}
          size="xs"
          state={savedPrinterMissing ? "error" : "default"}
        />
        {selectedPrinter?.value ? (
          <>
            {paperOptions.length > 1 ? (
              <RedoSingleSelectDropdownInput
                description={
                  newSettings
                    ? t(
                        "Choose a paper size supported by the selected printer.",
                      )
                    : undefined
                }
                label={t("Paper size")}
                options={paperOptions}
                optionSelected={({ item }) => {
                  input.inputs.paper.setValue(item.value ?? "");
                }}
                selectedItem={selectedPaper}
                size="xs"
              />
            ) : null}
            {savedPaperUnavailable ? (
              newSettings ? (
                <Flex dir="column" gap="xs">
                  <Text fontSize="xs" textColor="warning">
                    {`“${savedPaper}” is unavailable; printing will use this printer's default paper size.`}
                  </Text>
                  <Text fontSize="xs" textColor="tertiary">
                    {t(
                      "Choose an available size, or add this size in the computer's printer settings and refresh.",
                    )}
                  </Text>
                </Flex>
              ) : (
                <Text fontSize="xs" textColor="warning">
                  {savedPaperComputer
                    ? t(
                        'This printer doesn\'t report a "{{paperSize}}" size on {{computerName}}. It will print at the printer\'s default size until you pick an available size above, or add "{{paperSize}}" in the computer\'s printer settings and refresh.',
                        {
                          computerName: savedPaperComputer,
                          paperSize: savedPaper,
                        },
                      )
                    : t(
                        'This printer doesn\'t report a "{{paperSize}}" size. It will print at the printer\'s default size until you pick an available size above, or add "{{paperSize}}" in the computer\'s printer settings and refresh.',
                        { paperSize: savedPaper },
                      )}
                </Text>
              )
            ) : null}
            {binOptions.length > 1 ? (
              <RedoSingleSelectDropdownInput
                description={
                  newSettings
                    ? t("Choose which paper tray supplies this printer's jobs.")
                    : undefined
                }
                label={t("Printer bin")}
                options={binOptions}
                optionSelected={({ item }) => {
                  input.inputs.bin.setValue(item.value ?? "");
                }}
                selectedItem={selectedBin}
                size="xs"
              />
            ) : null}
            <RedoSingleSelectDropdownInput<0 | 90 | 180 | 270>
              description={
                newSettings
                  ? t("Rotate the document before sending it to the printer.")
                  : undefined
              }
              label={t("Rotate")}
              options={rotateOptions}
              optionSelected={({ item }) => {
                input.inputs.rotate.setValue(item.value);
              }}
              selectedItem={rotateOptions.find(
                (option) => option.value === input.inputs.rotate.value,
              )}
              size="xs"
            />

            {selectedPrinter.value?.capabilities?.dpis?.length ? (
              <RedoSingleSelectDropdownInput
                description={
                  newSettings
                    ? t("Choose a print resolution supported by this printer.")
                    : undefined
                }
                label={t("DPI")}
                options={dpiOptions}
                optionSelected={({ item }) => {
                  input.inputs.dpi.setValue(item.value ?? "");
                }}
                selectedItem={selectedDpi}
                size="xs"
              />
            ) : null}

            <RedoToggle
              description={
                newSettings
                  ? t("Scale the document to fit the selected paper size.")
                  : undefined
              }
              label={t("Fit to page")}
              layout={newSettings ? "trailing" : undefined}
              settingsStyle={newSettings}
              setValue={input.inputs.fit_to_page.setValue}
              size="xs"
              value={input.inputs.fit_to_page.value}
            />

            <RedoToggle
              description={t(
                "Send the file straight to the printer without {{productName}} re-rendering it.",
                { productName: "Redo Connect" },
              )}
              label={t("Raw printing")}
              settingsStyle={newSettings}
              setValue={input.inputs.useRaw.setValue}
              size="xs"
              value={input.inputs.useRaw.value}
            />

            <RedoSingleSelectDropdownInput<
              "" | "long-edge" | "short-edge" | "one-sided"
            >
              description={
                newSettings
                  ? t(
                      "Choose one-sided printing or how two-sided pages are flipped.",
                    )
                  : undefined
              }
              label={t("Duplex")}
              options={getDuplexOptions()}
              optionSelected={({ item }) => {
                input.inputs.duplex.setValue(item.value);
              }}
              selectedItem={getDuplexOptions().find(
                (option) => option.value === input.inputs.duplex.value,
              )}
              size="xs"
            />

            <RedoButton
              hierarchy="secondary"
              onClick={() => doTestPrint()}
              pending={testPrintLoad.pending}
              size="xs"
              text={t("Print test page")}
              theme="normal"
            />
          </>
        ) : null}
      </Flex>
    );
  },
);

const rotations = [0, 90, 180, 270] as const;
type Rotation = (typeof rotations)[number];

const rotateOptions: RedoListItem<Rotation>[] = [
  { id: 0, type: "text", text: "0°", value: 0 },
  { id: 90, type: "text", text: "90°", value: 90 },
  { id: 180, type: "text", text: "180°", value: 180 },
  { id: 270, type: "text", text: "270°", value: 270 },
];

type DuplexOption = "" | "long-edge" | "short-edge" | "one-sided";

function getDuplexOptions(): RedoListItem<DuplexOption>[] {
  return [
    { id: "default", type: "text", text: t("Default"), value: "" },
    { id: "one-sided", type: "text", text: t("One sided"), value: "one-sided" },
    {
      id: "short-edge",
      type: "text",
      text: t("Short edge"),
      value: "short-edge",
    },
    { id: "long-edge", type: "text", text: t("Long edge"), value: "long-edge" },
  ];
}
